import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PartnerVehicleIdentityFields from "../../components/partner/PartnerVehicleIdentityFields";
import {
  createPartnerDealerListing,
  fetchPartnerDealerListing,
  normalizePartnerDealerListing,
  updatePartnerDealerListing,
} from "../../services/automapPartnerDealerApi";
import {
  FUEL_OPTIONS,
  normalizeFuelForForm,
  normalizeFuelForSave,
  TRANSMISSION_OPTIONS,
} from "../../constants/automapDealerInventoryConstants";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  buildVehicleIdentitySubmitPayload,
  emptyVehicleIdentityState,
  IDENTITY_MODE,
  validateVehicleIdentity,
  vehicleIdentityFromListing,
} from "../../utils/partnerListingVehicleIdentity";
import { partnerListingDisplayTitle } from "../../utils/partnerListingDisplay";
import "../../styles/partnerDealers.css";

function primaryPhoto(listing) {
  const photo = listing?.photos?.find((entry) => entry.isPrimary) || listing?.photos?.[0];
  return photo?.url || null;
}

const EMPTY = {
  year: "",
  priceCop: "",
  mileageKm: "",
  transmission: "automatic",
  fuel: "gasolina",
  bodyType: "sedan",
  color: "",
  city: "",
  neighborhood: "",
  description: "",
  financingAvailable: false,
  availabilityStatus: "available",
  status: "active",
};

export default function PartnerDealerListingFormPage() {
  const { dealerId, listingId } = useParams();
  const [searchParams] = useSearchParams();
  const fromNormalization = searchParams.get("from") === "normalization";
  const isEdit = Boolean(listingId);
  const navigate = useNavigate();
  const { path } = usePartnerPortalBase();
  const identitySectionRef = useRef(null);
  const initialSnapshotRef = useRef({ form: EMPTY, identity: emptyVehicleIdentityState() });
  const [form, setForm] = useState(EMPTY);
  const [identity, setIdentity] = useState(emptyVehicleIdentityState());
  const [identityDirty, setIdentityDirty] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isLiveListing, setIsLiveListing] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [missingPhotos, setMissingPhotos] = useState(false);

  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;

    fetchPartnerDealerListing(dealerId, listingId)
      .then((result) => {
        if (cancelled) return;
        const listing = result.listing;
        setIsLiveListing(listing.dataSource === "vehicle_market");
        setPhotoUrl(primaryPhoto(listing));
        setMissingPhotos(Boolean(listing.missingPhotos));
        setIdentity(vehicleIdentityFromListing(listing));
        setIdentityDirty(false);
        const nextForm = {
          year: listing.year ?? "",
          priceCop: listing.priceCop ?? "",
          mileageKm: listing.mileageKm ?? "",
          transmission: listing.transmission || "automatic",
          fuel: normalizeFuelForForm(listing.fuel),
          bodyType: listing.bodyType || "sedan",
          color: listing.color || "",
          city: listing.city || "",
          neighborhood: listing.neighborhood || "",
          description: listing.description || "",
          financingAvailable: Boolean(listing.financingAvailable),
          availabilityStatus: listing.availabilityStatus || "available",
          status: listing.status || "active",
        };
        initialSnapshotRef.current = {
          form: nextForm,
          identity: vehicleIdentityFromListing(listing),
        };
        setForm(nextForm);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Unable to load listing.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dealerId, isEdit, listingId]);

  useEffect(() => {
    if (!fromNormalization || loading) return undefined;
    identitySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    return undefined;
  }, [fromNormalization, loading]);

  const handleChange = (field) => (event) => {
    const value =
      field === "financingAvailable" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleIdentityChange = (nextIdentity) => {
    setIdentity(nextIdentity);
    setIdentityDirty(true);
  };

  const hasListingFieldChanges = () => {
    const initial = initialSnapshotRef.current.form;
    return (
      String(form.priceCop ?? "") !== String(initial.priceCop ?? "") ||
      String(form.description ?? "") !== String(initial.description ?? "") ||
      String(form.status ?? "") !== String(initial.status ?? "")
    );
  };

  const hasIdentityChanges = () => {
    const initial = initialSnapshotRef.current.identity;
    return (
      String(identity.makeId ?? "") !== String(initial.makeId ?? "") ||
      String(identity.modelId ?? "") !== String(initial.modelId ?? "") ||
      String(identity.trimId ?? "") !== String(initial.trimId ?? "") ||
      String(identity.makeLabel ?? "") !== String(initial.makeLabel ?? "") ||
      String(identity.modelLabel ?? "") !== String(initial.modelLabel ?? "") ||
      String(identity.trimLabel ?? "") !== String(initial.trimLabel ?? "") ||
      String(identity.trimRaw ?? "") !== String(initial.trimRaw ?? "") ||
      identity.mode !== initial.mode
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isLiveListing || identityDirty) {
      const identityErrors = validateVehicleIdentity(identity);
      if (identityErrors.length) {
        setError(identityErrors.join(" "));
        return;
      }
    }

    if (
      isLiveListing &&
      identityDirty &&
      identity.mode === IDENTITY_MODE.MANUAL &&
      (!identity.makeId || !identity.modelId)
    ) {
      setError(
        "Imported listings must use the standard Make / Model catalog. Search and select from the list below, then enter trim text if needed."
      );
      return;
    }

    try {
      setSaving(true);
      setError("");

      if (isLiveListing) {
        const identityChanged = identityDirty || hasIdentityChanges();
        if (!identityChanged && !hasListingFieldChanges()) {
          setError("No changes to save.");
          return;
        }

        if (identityChanged) {
          await normalizePartnerDealerListing(dealerId, listingId, {
            makeId: identity.makeId,
            modelId: identity.modelId,
            trimId: identity.trimId ?? null,
            trimLabel: identity.trimLabel || null,
            trimRaw: identity.trimRaw || null,
            reason: fromNormalization
              ? "Manual vehicle name fix from normalization review"
              : "Manual vehicle name fix from listing editor",
            source: "partner_review_queue",
          });
        }

        if (hasListingFieldChanges()) {
          const livePayload = {
            priceCop: Number(form.priceCop),
            description: form.description,
            status: form.status,
          };
          await updatePartnerDealerListing(dealerId, listingId, livePayload);
        }
      } else {
        const payload = {
          ...form,
          year: Number(form.year),
          priceCop: Number(form.priceCop),
          mileageKm: Number(form.mileageKm),
          fuel: normalizeFuelForSave(form.fuel),
          publish: form.status === "active",
        };

        if (!isEdit || identityDirty) {
          Object.assign(payload, buildVehicleIdentitySubmitPayload(identity));
        }

        if (isEdit) {
          await updatePartnerDealerListing(dealerId, listingId, payload);
        } else {
          await createPartnerDealerListing(dealerId, payload);
        }
      }

      if (fromNormalization) {
        navigate(path(`dealers/${dealerId}/inventory/normalization`));
      } else {
        navigate(path(`dealers/${dealerId}/inventory`));
      }
    } catch (err) {
      setError(err.body?.error || err.body?.message || err.message || "Unable to save listing.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="muted panel-card">Loading listing…</p>;
  }

  const previewTitle = partnerListingDisplayTitle({
    year: form.year,
    makeLabel: identity.makeLabel,
    make: identity.makeRaw,
    makeRaw: identity.makeRaw,
    modelLabel: identity.modelLabel,
    model: identity.modelRaw,
    modelRaw: identity.modelRaw,
    trimLabel: identity.trimLabel,
    version: identity.trimRaw,
    trimRaw: identity.trimRaw,
  });

  return (
    <div className="partner-dealers-page panel-card">
      <header className="partner-dealers-page__header">
        <div>
          <Link className="cv-button-link" to={path(`dealers/${dealerId}/inventory`)}>
            ← Back to inventory
          </Link>
          <h2>{fromNormalization ? "Fix vehicle name" : isEdit ? "Edit listing" : "Add listing"}</h2>
          {fromNormalization ? (
            <p className="muted">
              Choose the correct Make, Model, and Trim from the catalog so this listing groups with similar
              vehicles in search and inventory views.
            </p>
          ) : isLiveListing ? (
            <p className="muted">
              Imported listing — price, description, and status can be updated here. Use the vehicle name
              section below to correct Make, Model, and Trim.
              {missingPhotos ? " Listings without photos stay paused and are hidden on AutoMapa." : ""}
            </p>
          ) : null}
        </div>
      </header>

      {error ? <p className="admin-data-health-error">{error}</p> : null}

      {isEdit ? (
        <div className="partner-dealer-form__preview">
          {photoUrl ? (
            <img className="partner-dealer-form__preview-image" src={photoUrl} alt={previewTitle || "Vehicle photo"} />
          ) : (
            <div className="partner-dealer-form__preview-placeholder">No photo available</div>
          )}
        </div>
      ) : null}

      <form className="partner-dealer-form" onSubmit={handleSubmit}>
        <div ref={identitySectionRef}>
          <PartnerVehicleIdentityFields
            identity={identity}
            onChange={handleIdentityChange}
            year={form.year}
            disabled={false}
            liveListing={isLiveListing}
          />
        </div>

        <div className="partner-dealer-form__grid">
          <label>
            Year *
            <input
              type="number"
              value={form.year}
              onChange={handleChange("year")}
              required
              readOnly={isLiveListing}
            />
          </label>
          <label>
            Price (COP) *
            <input type="number" value={form.priceCop} onChange={handleChange("priceCop")} required />
          </label>
          <label>
            Mileage (km) *
            <input
              type="number"
              value={form.mileageKm}
              onChange={handleChange("mileageKm")}
              required
              readOnly={isLiveListing}
            />
          </label>
          <label>
            City *
            <input value={form.city} onChange={handleChange("city")} required readOnly={isLiveListing} />
          </label>
          <label>
            Neighborhood
            <input value={form.neighborhood} onChange={handleChange("neighborhood")} />
          </label>
          <label>
            Transmission
            <select value={form.transmission} onChange={handleChange("transmission")} disabled={isLiveListing}>
              {TRANSMISSION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Fuel
            <select value={form.fuel} onChange={handleChange("fuel")} disabled={isLiveListing}>
              {FUEL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Body type
            <select value={form.bodyType} onChange={handleChange("bodyType")} disabled={isLiveListing}>
              <option value="sedan">Sedan</option>
              <option value="SUV">SUV</option>
              <option value="pickup">Pickup</option>
              <option value="hatchback">Hatchback</option>
            </select>
          </label>
          <label>
            Status
            <select value={form.status} onChange={handleChange("status")}>
              {!isLiveListing ? <option value="draft">Draft</option> : null}
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              {isLiveListing || (isEdit && !isLiveListing) ? <option value="sold">Sold</option> : null}
              {isEdit && !isLiveListing ? <option value="archived">Archived</option> : null}
            </select>
          </label>
        </div>

        <label className="partner-dealer-form__full">
          Description
          <textarea rows={4} value={form.description} onChange={handleChange("description")} />
        </label>

        {!isLiveListing ? (
          <label className="partner-dealer-form__checkbox">
            <input
              type="checkbox"
              checked={form.financingAvailable}
              onChange={handleChange("financingAvailable")}
            />
            Financing available
          </label>
        ) : null}

        <div className="partner-dealer-form__actions">
          <button type="submit" className="midpoint-share-button" disabled={saving}>
            {saving
              ? "Saving…"
              : fromNormalization
                ? "Save vehicle name"
                : isEdit
                  ? "Save listing"
                  : "Create listing"}
          </button>
          {fromNormalization ? (
            <Link className="cv-button-link" to={path(`dealers/${dealerId}/inventory/normalization`)}>
              Back to name matching
            </Link>
          ) : null}
        </div>
      </form>
    </div>
  );
}
